// The agent's mention in a record's comments (owner 2026-10-21: "@Sentinel" — the agent member's
// SERVED display name, never spelled here). Registered through CommentsSection's two seams:
//
// - `registerAgentMention`: the person's comment is posted as any comment (it mentions the agent by
//   its name) and a run is started for it — a durable Daimon turn on the record, as that person
//   (POST …/comments/agent-runs). Their rights and row scope apply; a record they cannot see is 404.
// - `registerAgentCommentExtras`: under the agent's answer, the run's changes as THE change card
//   (approvals/ChangeCard) with the person's Apply / Dismiss; after the thread, a quiet status line
//   ("<name> is working…" with its steps and an m:ss timer) while a run of theirs still works —
//   also after a reload, from the stored run.
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { monadClient, type AgentCard, type AgentRun, type CommentData } from '../api/client'
import { ChangeCard } from '../approvals/ChangeCard'
import { Button } from '../widgets/Button'
import { RichText } from '../widgets/richText'
import { registerAgentCommentExtras, registerAgentMention } from './CommentsSection'

const runsKey = (slug: string, recordId: string) => ['agent-runs', slug, recordId]

export function useAgentRuns(slug: string, typeId: string, recordId: string) {
  const queryClient = useQueryClient()
  const working = useRef(false)
  const result = useQuery({
    queryKey: runsKey(slug, recordId),
    queryFn: () => monadClient.listAgentRuns(slug, typeId, recordId),
    // while a run works, re-read it (the stored steps and, at the end, the posted answer)
    refetchInterval: (q) => ((q.state.data as AgentRun[] | undefined)?.some((r) => r.status === 'streaming') ? 1500 : false),
    staleTime: 5_000,
  })
  // a run that finishes has posted its answer as a comment: re-read the thread once, so it appears
  const streaming = (result.data ?? []).some((r) => r.status === 'streaming')
  useEffect(() => {
    if (working.current && !streaming) void queryClient.invalidateQueries({ queryKey: ['comments', slug, recordId] })
    working.current = streaming
  }, [streaming, queryClient, slug, recordId])
  return result
}

function useAgentName(slug: string): string | null {
  const { data } = useQuery({
    queryKey: ['daimon-capability', slug],
    queryFn: () => monadClient.getDaimonCapability(slug),
    staleTime: 5 * 60_000,
  })
  return (data as { agent?: { name?: string } | null } | undefined)?.agent?.name ?? null
}

function elapsed(since: string, now: number): string {
  const s = Math.max(0, Math.floor((now - Date.parse(since)) / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function Working({ run, name }: { run: AgentRun; name: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const last = run.tool_calls[run.tool_calls.length - 1]
  return (
    <div data-testid="agent-working" className="mt-2 text-xs text-muted-foreground">
      <p className="flex items-center gap-1.5">
        <span aria-hidden="true" className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
        <span>{name} is working…</span>
        <span className="tabular-nums" data-testid="agent-working-timer">{elapsed(run.started_at, now)}</span>
      </p>
      {last && <p data-testid="agent-working-step" className="ml-3">{last.label ?? last.tool}{last.summary ? ` → ${last.summary}` : ''}</p>}
    </div>
  )
}

function Card({ slug, typeId, recordId, card }: { slug: string; typeId: string; recordId: string; card: AgentCard }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const decide = async (decision: 'apply' | 'dismiss') => {
    setBusy(true)
    setError(null)
    try {
      if (decision === 'apply') await monadClient.applyConfirmation(slug, card.confirmation_id)
      else await monadClient.dismissConfirmation(slug, card.confirmation_id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reach the server.')
    } finally {
      setBusy(false)
      await queryClient.invalidateQueries({ queryKey: runsKey(slug, recordId) })
      queryClient.invalidateQueries({ queryKey: ['record', slug, typeId, recordId] })
    }
  }
  const review = card.on_apply === 'submit_for_review'
  return (
    <div className="mt-2" data-testid="agent-change">
      <ChangeCard card={card} slug={slug} error={error}
        note={review ? 'This needs review: Apply sends it to an admin.' : 'Nothing has changed yet. Apply makes the change with your rights.'}
        actions={card.status === 'pending' ? [
          { key: 'apply', label: review ? 'Submit for review' : 'Apply', variant: 'primary', disabled: busy, onClick: () => void decide('apply') },
          { key: 'dismiss', label: 'Dismiss', disabled: busy, onClick: () => void decide('dismiss') },
        ] : []} />
    </div>
  )
}

/** Under the agent's answer: its run's change cards. With ``comment`` null (after the thread): the
 *  status line of the person's runs that still work. */
/** The agent's answer before anyone else sees it: the mentioner's private draft and its "Post reply". */
function Draft({ slug, typeId, recordId, run, name }: { slug: string; typeId: string; recordId: string; run: AgentRun; name: string }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const post = async () => {
    setBusy(true)
    setError(null)
    try {
      await monadClient.postAgentReply(slug, typeId, recordId, run.run_id)
      await queryClient.invalidateQueries({ queryKey: ['comments', slug, recordId] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not post the reply.')
    } finally {
      setBusy(false)
      queryClient.invalidateQueries({ queryKey: runsKey(slug, recordId) })
    }
  }
  return (
    <div data-testid="agent-draft" className="mt-2 rounded-md border border-dashed border-border p-2 text-sm">
      <p className="text-xs text-muted-foreground">{name}'s answer — only you can see it until you post it</p>
      <RichText text={run.draft ?? ""} className="mt-1" />
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      <div className="mt-2 flex">
        <Button variant="primary" size="sm" data-testid="agent-draft-post" disabled={busy} className="ml-auto"
          onClick={() => void post()}>{busy ? 'Posting…' : 'Post reply'}</Button>
      </div>
    </div>
  )
}

/** Under the person's mention: their run's private draft (with "Post reply") and its change cards.
 *  With ``comment`` null (after the thread): the status line of their runs that still work. */
export function AgentCommentExtras({ slug, typeId, recordId, comment }: {
  slug: string; typeId: string; recordId: string; comment: CommentData | null
}) {
  const { data: runs } = useAgentRuns(slug, typeId, recordId)
  const name = useAgentName(slug)
  if (!runs || !name) return null
  if (comment === null) {
    return <>{runs.filter((r) => r.status === 'streaming').map((r) => <Working key={r.run_id} run={r} name={name} />)}</>
  }
  const run = runs.find((r) => r.comment_id === comment.comment_id)
  if (!run) return null
  return <>
    {run.status !== 'streaming' && !run.answer_comment_id && run.draft
      && <Draft slug={slug} typeId={typeId} recordId={recordId} run={run} name={name} />}
    {run.confirmations.map((c) => <Card key={c.confirmation_id} slug={slug} typeId={typeId} recordId={recordId} card={c} />)}
  </>
}

/** A mention whose comment was posted but whose run did not start (a network error): retried with the
 *  SAME comment, so the person's comment is never posted twice. Keyed by record and text. */
const unstarted = new Map<string, string>()

/** Turn the agent's mention on: the composer's @<name> posts the person's comment and runs the agent. */
export function registerAgentMentions(): void {
  registerAgentMention(async ({ slug, typeId, recordId, text, agentName }) => {
    const mention = `@${agentName}`
    const body = text.includes(mention) ? text : `${mention} ${text}`.trim()
    const key = `${slug}|${recordId}|${body}`
    let commentId = unstarted.get(key)
    if (!commentId) {
      commentId = (await monadClient.addComment(slug, typeId, recordId, { body })).comment_id
      unstarted.set(key, commentId)
    }
    await monadClient.startAgentRun(slug, typeId, recordId, commentId)
    unstarted.delete(key)
  })
  registerAgentCommentExtras(AgentCommentExtras)
}
